/**
 * SmartWidget — widget sidebar SMART avec 3 KPIs + sparkline + drill-down.
 *
 * PHASE 3.0 UX Revolution (DECISIONS #167).
 * V4-PREMIUM : accent stripe Samsung blue, gradient bg, top highlight, icon glow.
 */

import { useAnimate, motion } from 'framer-motion';
import { Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { peauWidget, DecorWidgetActif } from './peauWidget';
import { InlineSparkline } from './InlineSparkline';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface KpiSlot {
  value: string;
  label: string;
  delta?: number;
}

export interface SmartWidgetProps {
  id: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  subLabel: string;
  delta?: number;
  sparklineData?: number[];
  kpi2?: KpiSlot;
  kpi3?: KpiSlot;
  isActive?: boolean;
  isDimmed?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function sparkColor(delta?: number): string {
  if (delta === undefined) return '#64748b';
  if (delta > 0) return '#22c55e';
  if (delta < 0) return '#ef4444';
  return '#64748b';
}

/** Couleur texte valeur KPI : vert si hausse, rouge si baisse, sinon neutre. */
function kpiValueClass(delta?: number, isActive = false): string {
  if (delta !== undefined && delta > 0) return 'text-accent-success';
  if (delta !== undefined && delta < 0) return 'text-accent-danger';
  return isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-100' : 'text-foreground';
}

function DeltaTag({ delta }: { delta: number }) {
  const pos = delta > 0;
  const neg = delta < 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5',
        'text-[7.5px] font-semibold tabular-nums leading-none',
        pos && 'bg-accent-success/15 text-accent-success',
        neg && 'bg-accent-danger/15 text-accent-danger',
        !pos && !neg && 'bg-muted/80 text-muted-foreground',
      )}
    >
      {pos ? '↑' : neg ? '↓' : '─'}
      {' '}{Math.abs(delta).toFixed(1)} pt
    </span>
  );
}

// ── SmartWidget ────────────────────────────────────────────────────────────────

export function SmartWidget({
  id,
  icon,
  label,
  value,
  subLabel,
  delta,
  sparklineData,
  kpi2,
  kpi3,
  isActive = false,
  // isDimmed kept in props for API compat but unused visually
  isLoading = false,
  onClick,
}: SmartWidgetProps) {
  const [scope, animate] = useAnimate();

  function handleClick() {
    onClick();
    try {
      // Ripple circulaire — sensation de "lancement"
      void animate('.sw-ripple',
        { scale: [0, 4.5], opacity: [0.45, 0] },
        { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      );
      // Shimmer gauche→droite — direction du slide de page
      void animate('.sw-shimmer', { x: [-90, 300] }, { duration: 0.48, ease: [0.25, 0.46, 0.45, 0.94] });
      void animate('.sw-header', { x: [-26, 0], opacity: [0.2, 1] },                { duration: 0.22, ease: [0.22, 1, 0.36, 1] });
      void animate('.sw-kpi1',   { x: [-32, 0], opacity: [0.1, 1], scale: [0.88, 1] }, { duration: 0.28, delay: 0.05, ease: [0.22, 1, 0.36, 1] });
      void animate('.sw-kpi2',   { x: [-22, 0], opacity: [0.1, 1] },                { duration: 0.24, delay: 0.12, ease: [0.22, 1, 0.36, 1] });
      void animate('.sw-kpi3',   { x: [-16, 0], opacity: [0.1, 1] },                { duration: 0.24, delay: 0.18, ease: [0.22, 1, 0.36, 1] });
      void animate('.sw-spark',  { opacity: [0.05, 1], y: [7, 0] },                 { duration: 0.22, delay: 0.24 });
    } catch { /* navigation ne doit jamais être bloquée */ }
  }

  return (
    <motion.button
      ref={scope}
      type="button"
      data-testid={`smart-widget-${id}`}
      onClick={handleClick}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 600, damping: 35 }}
      className={cn(peauWidget(isActive))}
      aria-pressed={isActive}
    >

      {/* ── Active decorations ───────────────────────────────────────────── */}
      {isActive && (
        <DecorWidgetActif />
      )}

      {/* ── Brain icon — indicateur SMART (top-right)
          Blanc/50 par défaut · bleu Samsung au hover (cohérent avec border + bg hover) */}
      <Brain
        className="pointer-events-none absolute right-2 top-2 h-4 w-4 text-brand-samsung-600/70 dark:text-white/50 transition-colors duration-200 group-hover:text-brand-samsung-400"
        aria-hidden
      />

      {/* ── Ripple de lancement ──────────────────────────────────────────── */}
      <span
        className="sw-ripple pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-samsung-400/0 opacity-0"
        aria-hidden
      />

      {/* ── Shimmer clip ─────────────────────────────────────────────────── */}
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl" aria-hidden>
        <span
          className="sw-shimmer absolute inset-y-0 left-0 w-28 bg-gradient-to-r from-transparent via-brand-samsung-400/25 to-transparent"
          style={{ transform: 'translateX(-112px)' }}
        />
      </span>

      {/* ── Header : icône + label ───────────────────────────────────────── */}
      <div className="sw-header flex items-center gap-1.5 mb-1.5 shrink-0 relative">
        <span
          className={cn(
            'shrink-0 flex h-[20px] w-[20px] items-center justify-center rounded-md',
            '[&_svg]:h-3.5 [&_svg]:w-3.5 transition-all duration-200',
            isActive
              ? 'bg-brand-samsung-500/35 text-brand-samsung-700 dark:text-brand-samsung-200 shadow-[0_0_8px_rgba(20,40,160,0.45)]'
              : 'bg-white/[0.05] text-muted-foreground/40',
          )}
        >
          {icon}
        </span>
        <span className={cn(
          'text-[9px] font-semibold uppercase tracking-widest truncate transition-colors duration-200',
          isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-300/90' : 'text-muted-foreground/70',
        )}>
          {label}
        </span>
      </div>

      {/* ── Corps ───────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex flex-col justify-between relative">
        {isLoading ? (
          <div className="grid grid-cols-3 gap-1.5 mt-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded-lg bg-muted/60" />
            ))}
          </div>
        ) : (
          <>
            {/* ── 3 KPIs ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-3 gap-x-1.5">

              {/* KPI 1 */}
              <div className="sw-kpi1 text-center">
                <div
                  data-testid={`widget-value-${id}`}
                  className={cn(
                    'text-[15px] font-bold tabular-nums leading-none transition-colors duration-200',
                    kpiValueClass(delta, isActive),
                  )}
                >
                  {value}
                </div>
                <div className="text-[8px] text-muted-foreground/60 truncate mt-0.5 leading-none">
                  {subLabel}
                </div>
                {delta !== undefined && (
                  <div data-testid={`widget-delta-${id}`} className="mt-1 flex justify-center">
                    <DeltaTag delta={delta} />
                  </div>
                )}
              </div>

              {/* KPI 2 */}
              <div className="sw-kpi2 border-l border-[var(--border-default,var(--surface-border))]/50 text-center">
                <div className={cn('text-[13px] font-bold tabular-nums leading-none', kpiValueClass(kpi2?.delta))}>
                  {kpi2?.value ?? '—'}
                </div>
                <div className="text-[8px] text-muted-foreground/60 truncate mt-0.5 leading-none">
                  {kpi2?.label ?? '·'}
                </div>
                {kpi2?.delta !== undefined && (
                  <div className="mt-1 flex justify-center"><DeltaTag delta={kpi2.delta} /></div>
                )}
              </div>

              {/* KPI 3 */}
              <div className="sw-kpi3 border-l border-[var(--border-default,var(--surface-border))]/50 text-center">
                <div className={cn('text-[13px] font-bold tabular-nums leading-none', kpiValueClass(kpi3?.delta))}>
                  {kpi3?.value ?? '—'}
                </div>
                <div className="text-[8px] text-muted-foreground/60 truncate mt-0.5 leading-none">
                  {kpi3?.label ?? '·'}
                </div>
                {kpi3?.delta !== undefined && (
                  <div className="mt-1 flex justify-center"><DeltaTag delta={kpi3.delta} /></div>
                )}
              </div>
            </div>

            {/* ── Sparkline ────────────────────────────────────────────── */}
            {sparklineData && sparklineData.length >= 2 && (
              <div
                data-testid={`widget-sparkline-${id}`}
                className="sw-spark mt-auto pt-1.5 border-t border-[var(--border-default,var(--surface-border))]/25"
              >
                <InlineSparkline
                  data={sparklineData}
                  width={210}
                  height={12}
                  color={sparkColor(delta)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </motion.button>
  );
}
